<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PaymentService } from '@/services/payment.services';

const props = defineProps<{
  open: boolean;
  paymentId: string | null;
  maxAmount: number | null;
  currency?: string | null;
}>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'refunded'): void;
}>();

const { t } = useI18n({ useScope: 'global' });

const refundType = ref<'full' | 'partial'>('full');
const partialAmount = ref<number | null>(null);
const reason = ref('');
const isSaving = ref(false);
const error = ref('');
const success = ref(false);

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      refundType.value = 'full';
      partialAmount.value = null;
      reason.value = '';
      error.value = '';
      success.value = false;
    }
  },
);

function formatAmount(amount: number | null | undefined) {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: props.currency ?? 'EUR',
    }).format(amount);
  } catch {
    return `${amount} ${props.currency ?? ''}`;
  }
}

async function submit() {
  if (!props.paymentId) return;
  if (!reason.value.trim()) {
    error.value = t('paymentsMgmt.refundReasonRequired');
    return;
  }
  if (refundType.value === 'partial') {
    if (!partialAmount.value || partialAmount.value <= 0) {
      error.value = t('paymentsMgmt.refundAmountRequired');
      return;
    }
    if (props.maxAmount !== null && partialAmount.value > props.maxAmount) {
      error.value = t('paymentsMgmt.refundAmountTooHigh');
      return;
    }
  }
  isSaving.value = true;
  error.value = '';
  try {
    await PaymentService.refundPayment(
      props.paymentId,
      reason.value.trim(),
      refundType.value === 'partial'
        ? (partialAmount.value ?? undefined)
        : undefined,
    );
    success.value = true;
    emit('refunded');
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    error.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('paymentsMgmt.refundError');
  } finally {
    isSaving.value = false;
  }
}

function close() {
  emit('close');
}
</script>

<template>
  <Teleport to="body">
    <Transition name="rf-backdrop">
      <div v-if="open" class="rf-backdrop" @click="close" />
    </Transition>
    <Transition name="rf-modal">
      <div v-if="open" class="rf-modal" @click.stop>
        <div class="rf__header">
          <h3 class="rf__title">
            <i class="pi pi-replay" /> {{ t('paymentsMgmt.refundTitle') }}
          </h3>
          <button class="rf__close" @click="close">
            <i class="pi pi-times" />
          </button>
        </div>

        <div class="rf__body">
          <p v-if="success" class="rf__success">
            <i class="pi pi-check-circle" />
            {{ t('paymentsMgmt.refundSuccess') }}
          </p>

          <template v-else>
            <div class="rf__choice-group">
              <button
                class="rf__choice"
                :class="{ 'rf__choice--active': refundType === 'full' }"
                @click="refundType = 'full'"
              >
                <i class="pi pi-check-circle" />
                <div>
                  <span class="rf__choice-title">{{
                    t('paymentsMgmt.refundFull')
                  }}</span>
                  <span class="rf__choice-desc">{{
                    formatAmount(maxAmount)
                  }}</span>
                </div>
              </button>
              <button
                class="rf__choice"
                :class="{ 'rf__choice--active': refundType === 'partial' }"
                @click="refundType = 'partial'"
              >
                <i class="pi pi-percentage" />
                <div>
                  <span class="rf__choice-title">{{
                    t('paymentsMgmt.refundPartial')
                  }}</span>
                  <span class="rf__choice-desc">{{
                    t('paymentsMgmt.refundPartialDesc')
                  }}</span>
                </div>
              </button>
            </div>

            <div v-if="refundType === 'partial'" class="rf__field">
              <label class="rf__label">{{
                t('paymentsMgmt.refundAmount')
              }}</label>
              <input
                v-model.number="partialAmount"
                type="number"
                step="0.01"
                :max="maxAmount ?? undefined"
                class="rf__input"
                :placeholder="formatAmount(maxAmount)"
              />
            </div>

            <div class="rf__field">
              <label class="rf__label">{{
                t('paymentsMgmt.refundReason')
              }}</label>
              <textarea
                v-model="reason"
                class="rf__textarea"
                rows="3"
                :placeholder="t('paymentsMgmt.refundReasonPlaceholder')"
              />
            </div>

            <p v-if="error" class="rf__error">
              <i class="pi pi-exclamation-circle" /> {{ error }}
            </p>
          </template>
        </div>

        <div class="rf__actions">
          <button class="rf__btn rf__btn--ghost" @click="close">
            {{ success ? t('paymentsMgmt.close') : t('paymentsMgmt.cancel') }}
          </button>
          <button
            v-if="!success"
            class="rf__btn rf__btn--danger"
            :disabled="isSaving"
            @click="submit"
          >
            <span v-if="isSaving" class="rf__spinner" />
            {{
              isSaving
                ? t('paymentsMgmt.refundInProgress')
                : t('paymentsMgmt.refundConfirm')
            }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.rf-backdrop {
  position: fixed;
  inset: 0;
  z-index: 950;
  background: rgba(8, 12, 20, 0.5);
  backdrop-filter: blur(3px);
}
.rf-backdrop-enter-active,
.rf-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.rf-backdrop-enter-from,
.rf-backdrop-leave-to {
  opacity: 0;
}

.rf-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 951;
  width: min(420px, 92vw);
  max-height: 85vh;
  overflow-y: auto;
  background: #f4f6f9;
  border-radius: 1.5rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
}
[data-theme='dark'] .rf-modal {
  background: #111c2e;
}
.rf-modal-enter-active,
.rf-modal-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.rf-modal-enter-from,
.rf-modal-leave-to {
  opacity: 0;
  transform: translate(-50%, -48%);
}

.rf__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1rem;
}
.rf__title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.rf__close {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  flex-shrink: 0;
}

.rf__body {
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.rf__choice-group {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.rf__choice {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  background: var(--nxt-card-bg);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  color: var(--nxt-on-surface-muted);
}
.rf__choice i {
  font-size: 1.125rem;
}
.rf__choice--active {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.1);
  color: #0b1320;
}
.rf__choice-title {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.rf__choice-desc {
  display: block;
  font-size: 0.75rem;
}

.rf__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.rf__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.rf__input,
.rf__textarea {
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}
.rf__textarea {
  resize: vertical;
}
.rf__input:focus,
.rf__textarea:focus {
  border-color: #ffd60a;
}

.rf__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-error);
  margin: 0;
}
.rf__success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f7b0f;
  background: rgba(16, 181, 16, 0.12);
  margin: 0;
}

.rf__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem 1.5rem;
}
.rf__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
}
.rf__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.rf__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.rf__btn--danger {
  background: var(--nxt-error);
  color: #fff;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.35);
}
.rf__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  animation: rf-spin 0.7s linear infinite;
}
@keyframes rf-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
[data-theme='dark'] .rf__choice {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .rf__close,
[data-theme='dark'] .rf__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .rf__input,
[data-theme='dark'] .rf__textarea {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
</style>
